import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, ActivityIndicator,
  TouchableOpacity, TextInput, Modal, ScrollView, Platform,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api, apiPost, apiDelete } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive, rs, fs } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';

interface ControllerItem {
  id: string; name: string | null; firmwareVersion: string | null;
  isOnline: boolean; lastSeen: string | null;
  businessName: string; createdAt: string;
  claimedAt: string | null;
  otaStatus: string | null; otaVersion: string | null;
  bayId: string | null;
  bayName: string | null;
  locationName: string | null;
  relayCount: number;
  /** 1.4 P04 */
  model?: string;
  modelLabel?: string;
  reportedModel?: string | null;
}

const MODEL_OPTIONS: { id: string; label: string }[] = [
  { id: 'DTW-4R', label: 'RCP-Alpha-PoE' },
  { id: 'WS-8DI-8RO', label: 'RCP-Beta-PoE' },
];

/** AdminControllers - list, search, claim, and manage ESP32 controllers platform-wide. */
export default function AdminControllers() {
  const { colors } = useTheme();
  const { width } = useResponsive();
  const isMobile = width < 768;
  const styles = useStyles(colors, isMobile);
  const modalBottomPadding = useModalBottomPadding();
  const router = useRouter();
  const { ownerId: paramOwnerId } = useLocalSearchParams<{ ownerId?: string }>();
  const [ctrls, setCtrls] = useState<ControllerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [showQr, setShowQr] = useState<{ id: string; qrCode: string; mqttUsername?: string; mqttPassword?: string } | null>(null);
  const [batchCount, setBatchCount] = useState('');
  const [showBatch, setShowBatch] = useState(false);
  const [batchResults, setBatchResults] = useState<any[]>([]);
  const [customId, setCustomId] = useState('');
  const [genModel, setGenModel] = useState('DTW-4R');
  const [qrLoading, setQrLoading] = useState('');
  const [deleting, setDeleting] = useState('');

  const fetchData = useCallback(async (p = 1, s = '', ownerFilter = paramOwnerId, signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      if (ownerFilter) params.set('ownerId', ownerFilter);
      const data = await api<{ controllers: ControllerItem[]; total: number }>(`/api/admin/controllers?${params}`);
      if (signal?.aborted) return;
      setCtrls(data.controllers);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, [paramOwnerId]);

  useEffect(() => { const ac = new AbortController(); fetchData(1, '', paramOwnerId, ac.signal); return () => ac.abort(); }, [paramOwnerId]);

  const csvEndpoint = `/api/admin/controllers?${new URLSearchParams({ ...(search && { search }) })}`;

  const generateController = async () => {
    setGenerating(true);
    try {
      const body: any = { model: genModel };
      if (customId.trim()) body.id = customId.trim();
      const result = await apiPost<{ id: string; qrCode: string; mqttUsername: string; mqttPassword: string }>('/api/admin/controllers/generate', body);
      setShowQr({ id: result.id, qrCode: result.qrCode, mqttUsername: result.mqttUsername, mqttPassword: result.mqttPassword });
      setCustomId('');
      fetchData(page, search);
    } catch (err: any) {
      alert(err.message || 'Failed to generate controller');
    } finally {
      setGenerating(false);
    }
  };

  const generateBatch = async () => {
    const count = parseInt(batchCount);
    if (!count || count < 1 || count > 100) {
      alert('Enter a number between 1 and 100');
      return;
    }
    setGenerating(true);
    try {
      const result = await apiPost<{ controllers: Array<{ id: string; qrCode: string }> }>('/api/admin/controllers/generate-batch', { count, model: genModel });
      setBatchResults(result.controllers || []);
      fetchData(page, search);
    } catch (err: any) {
      alert(err.message || 'Failed to generate batch');
    } finally {
      setGenerating(false);
    }
  };

  const viewQrCode = async (item: ControllerItem) => {
    setQrLoading(item.id);
    try {
      const result = await api<{ qrCode: string; id: string }>(
        `/api/admin/controllers/${item.id}/qr`
      );
      setShowQr({ id: result.id, qrCode: result.qrCode });
    } catch (err: any) {
      alert(err.message || 'Failed to load QR code');
    } finally {
      setQrLoading('');
    }
  };

  const deleteController = (item: ControllerItem) => {
    const name = item.name || item.id;
    if (!confirm(`Permanently delete controller "${name}"?\n\nThis will unassign it from any bays and remove all its logs. This cannot be undone.`)) return;
    setDeleting(item.id);
    apiDelete(`/api/admin/controllers/${item.id}`)
      .then(() => {
        fetchData(page, search);
      })
      .catch((err: any) => {
        alert(err.message || 'Failed to delete controller');
      })
      .finally(() => {
        setDeleting('');
      });
  };

  const renderItem = ({ item }: { item: ControllerItem }) => (
    <TouchableOpacity
      style={styles.row}
      activeOpacity={0.7}
      onPress={() => router.push(`/admin/controllers/${item.id}` as never)}
    >
      <View style={[styles.statusDot, { backgroundColor: item.isOnline ? '#059669' : '#dc2626' }]} />
      <View style={styles.rowBody}>
        <View style={styles.rowHeader}>
          <Text style={styles.name}>{item.name || item.id}</Text>
          <View style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
            {item.otaStatus === 'pending' && (
              <View style={styles.otaBadge}>
                <Text style={styles.otaText}>OTA: {item.otaVersion}</Text>
              </View>
            )}
            <View style={styles.fwBadge}>
              <Text style={styles.fwText}>{item.firmwareVersion || 'n/a'}</Text>
            </View>
          </View>
        </View>
        <Text style={styles.meta}>
          ID: {item.id} · {item.isOnline ? 'Online' : 'Offline'} · {item.modelLabel || item.model || 'RCP-Alpha-PoE'} ({item.relayCount}R)
          {item.reportedModel ? ` · reports ${item.reportedModel}!` : ''}
        </Text>
        {(item.bayName || item.locationName) && (
          <Text style={styles.sub}>
            <Ionicons name="location" size={11} color={colors.primary} />
            {' '}{item.locationName || 'Unknown location'}
            {item.bayName ? ` · ${item.bayName}` : ''}
          </Text>
        )}
        <Text style={styles.sub}>
          {item.claimedAt ? (
            <>Owner: {item.businessName || 'Claimed'} · Claimed {new Date(item.claimedAt).toLocaleDateString()}</>
          ) : (
            <Text style={{ color: '#d97706' }}>Unclaimed</Text>
          )}
          {item.lastSeen ? ` · Last seen: ${new Date(item.lastSeen).toLocaleString()}` : ''}
        </Text>
        <View style={styles.rowActions}>
          <TouchableOpacity
            style={styles.rowActionBtn}
            onPress={() => viewQrCode(item)}
            disabled={qrLoading === item.id}
          >
            {qrLoading === item.id ? (
              <ActivityIndicator size="small" color={colors.primary} />
            ) : (
              <>
                <Ionicons name="qr-code" size={14} color={colors.primary} />
                <Text style={styles.rowActionText}>QR</Text>
              </>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.rowActionBtn, styles.rowActionDanger]}
            onPress={() => deleteController(item)}
            disabled={deleting === item.id}
          >
            {deleting === item.id ? (
              <ActivityIndicator size="small" color="#dc2626" />
            ) : (
              <>
                <Ionicons name="trash-outline" size={14} color="#dc2626" />
                <Text style={[styles.rowActionText, { color: '#dc2626' }]}>Delete</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      {/* Custom ID input on its own line */}
      <TextInput
        style={[styles.customIdInput, { color: colors.text }]}
        placeholder="Custom ID (optional)"
        placeholderTextColor={colors.textLight}
        value={customId}
        onChangeText={setCustomId}
        maxLength={64}
      />
      {/* Model picker (1.4 P04): applies to single and batch generation */}
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {MODEL_OPTIONS.map(m => (
          <TouchableOpacity
            key={m.id}
            onPress={() => setGenModel(m.id)}
            style={{
              paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, borderWidth: 1,
              borderColor: genModel === m.id ? colors.primary : colors.border,
              backgroundColor: genModel === m.id ? colors.primary + '20' : 'transparent',
            }}
          >
            <Text style={{ color: genModel === m.id ? colors.primary : colors.textSecondary, fontSize: fs(12), fontWeight: '600' }}>{m.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
      {/* Action buttons */}
      <View style={styles.actionRow}>
        <TouchableOpacity
          style={[styles.genBtn, generating && { opacity: 0.6 }]}
          onPress={generateController}
          disabled={generating}
        >
          {generating ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <>
              <Ionicons name="add-circle" size={18} color="#fff" />
              <Text style={styles.genBtnText}>Generate Controller</Text>
            </>
          )}
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.batchBtn}
          onPress={() => setShowBatch(!showBatch)}
        >
          <Ionicons name="layers" size={18} color={colors.primary} />
          <Text style={styles.batchBtnText}>Batch</Text>
        </TouchableOpacity>
      </View>

      {showBatch && (
        <View style={styles.batchCard}>
          <Text style={styles.batchLabel}>Generate multiple controllers at once:</Text>
          <View style={{ flexDirection: 'row', gap: spacing.sm, alignItems: 'center' }}>
            <TextInput
              style={[styles.batchInput, { color: colors.text }]}
              placeholder="Count (1-100)"
              placeholderTextColor={colors.textLight}
              value={batchCount}
              onChangeText={setBatchCount}
              keyboardType="numeric"
              maxLength={3}
            />
            <TouchableOpacity style={styles.batchSubmit} onPress={generateBatch} disabled={generating}>
              <Text style={styles.batchSubmitText}>Generate</Text>
            </TouchableOpacity>
          </View>
          {batchResults.length > 0 && (
            <View style={styles.batchResults}>
              <Text style={styles.batchResultTitle}>{batchResults.length} controllers generated:</Text>
              {batchResults.map((r: any) => (
                <View key={r.id} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 2 }}>
                  <Text style={styles.batchResultId}>{r.id}</Text>
                  {r.qrCode && (
                    <TouchableOpacity onPress={() => setShowQr({ id: r.id, qrCode: r.qrCode })}>
                      <Text style={{ color: colors.primary, fontSize: fs(12), fontWeight: '600' }}>View QR</Text>
                    </TouchableOpacity>
                  )}
                </View>
              ))}
            </View>
          )}
        </View>
      )}

      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={() => fetchData(1, search)}
        total={total} page={page} limit={20} onPageChange={(p) => fetchData(p, search)}
        csvEndpoint={csvEndpoint} csvFilename="controllers.csv" label="controller"
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />} data={ctrls} keyExtractor={item => item.id} renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No controllers found</Text>}
        />
      )}

      {/* QR Code Modal */}
      {showQr && (
        <Modal visible transparent animationType="fade" onRequestClose={() => setShowQr(null)}>
          <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowQr(null)}>
            <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
              <Text style={styles.modalTitle}>Controller QR Code</Text>
              <Text style={styles.modalId} selectable>Device ID: {showQr.id}</Text>
              {showQr.mqttUsername && (
                <View style={styles.credentialsBox}>
                  <Text style={styles.credLabel}>MQTT Username</Text>
                  <Text style={styles.credValue} selectable>{showQr.mqttUsername}</Text>
                  <Text style={[styles.credLabel, { marginTop: 6 }]}>MQTT Password</Text>
                  <Text style={styles.credValue} selectable>{showQr.mqttPassword}</Text>
                </View>
              )}
              {showQr.qrCode && (
                <View style={styles.qrContainer}>
                  <img
                    src={showQr.qrCode}
                    alt="QR Code"
                    style={{ width: 300 }}
                  />
                </View>
              )}
              {showQr.qrCode && (
                <TouchableOpacity
                  style={styles.downloadBtn}
                  onPress={async () => {
                    try {
                      const { downloadFile } = await import('../../src/utils/fileExport');
                      await downloadFile(showQr.qrCode, `controller-${showQr.id}.svg`);
                    } catch (err) {
                      console.error('QR download failed:', err);
                    }
                  }}
                >
                  <Ionicons name="download-outline" size={18} color="#fff" />
                  <Text style={styles.downloadBtnText}>Download SVG</Text>
                </TouchableOpacity>
              )}
              <Text style={styles.modalHint}>
                Scan this QR code with the RinsePay owner app to claim the controller.
              </Text>
              <TouchableOpacity style={styles.modalCloseBtn} onPress={() => setShowQr(null)}>
                <Text style={styles.modalCloseBtnText}>Close</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </Modal>
      )}
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  customIdInput: {
    backgroundColor: c.background, borderWidth: 1, borderColor: c.borderLight,
    borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 8,
    fontSize: fontSize.sm, marginBottom: spacing.sm, width: '100%',
  },
  actionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md },
  genBtn: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    backgroundColor: c.primary, paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.md,
  },
  genBtnText: { color: '#fff', fontWeight: fontWeight.semibold, fontSize: fontSize.sm },
  batchBtn: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    borderWidth: 1, borderColor: c.borderLight, backgroundColor: c.surface,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md,
  },
  batchBtnText: { color: c.primary, fontWeight: fontWeight.medium, fontSize: fontSize.sm },
  batchCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.md, borderWidth: 1, borderColor: c.borderLight,
  },
  batchLabel: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.sm },
  batchInput: {
    flex: 1, backgroundColor: c.background, borderWidth: 1, borderColor: c.borderLight,
    borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 8, fontSize: fontSize.sm,
  },
  batchSubmit: {
    backgroundColor: c.primary, paddingHorizontal: spacing.md, paddingVertical: 8,
    borderRadius: borderRadius.sm,
  },
  batchSubmitText: { color: '#fff', fontWeight: fontWeight.semibold, fontSize: fontSize.sm },
  batchResults: { marginTop: spacing.sm, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight },
  batchResultTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginBottom: 4 },
  batchResultId: { fontSize: fontSize.xs, color: c.text, fontFamily: 'monospace', paddingVertical: 1 },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: 10 },
  statusDot: { width: 12, height: 12, borderRadius: 6 },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.text, flex: 1 },
  fwBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8, backgroundColor: c.surfaceSecondary },
  fwText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.textSecondary },
  otaBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8, backgroundColor: '#fef3c7' },
  otaText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: '#d97706' },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  sub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  rowActions: { flexDirection: 'row', gap: spacing.sm, marginTop: 6 },
  rowActionBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 8, paddingVertical: 4, borderRadius: borderRadius.sm,
    backgroundColor: c.primary + '10', borderWidth: 1, borderColor: c.primary + '25',
  },
  rowActionDanger: { backgroundColor: '#dc262610', borderColor: '#dc262625' },
  rowActionText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.primary },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: isMobile ? 'flex-end' : 'center', alignItems: 'center', padding: isMobile ? 0 : undefined,
  },
  modalContent: {
    backgroundColor: c.surface, borderRadius: isMobile ? 0 : borderRadius.lg, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg, padding: spacing.xl,
    maxWidth: isMobile ? undefined : 400, width: isMobile ? '100%' : '90%', alignItems: 'center',
  },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  modalId: { fontSize: fs(10), color: c.text, fontFamily: 'monospace', marginBottom: spacing.sm, textAlign: 'center', flexWrap: 'wrap' },
  credentialsBox: {
    backgroundColor: c.background, borderWidth: 1, borderColor: c.borderLight,
    borderRadius: borderRadius.sm, padding: spacing.sm, marginBottom: spacing.md, width: '100%',
  },
  credLabel: { fontSize: fs(10), color: c.textSecondary, fontWeight: fontWeight.medium, textTransform: 'uppercase', letterSpacing: 0.5 },
  credValue: { fontSize: fs(12), color: c.text, fontFamily: 'monospace', marginTop: 2 },
  qrContainer: { marginVertical: spacing.md },
  modalHint: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginBottom: spacing.md },
  downloadBtn: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    backgroundColor: c.primary, paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.md, marginBottom: spacing.sm,
  },
  downloadBtnText: { color: '#fff', fontWeight: fontWeight.semibold, fontSize: fontSize.sm },
  modalCloseBtn: {
    backgroundColor: c.primary, paddingHorizontal: spacing.xl, paddingVertical: spacing.sm,
    borderRadius: borderRadius.md,
  },
  modalCloseBtnText: { color: '#fff', fontWeight: fontWeight.semibold, fontSize: fontSize.md },
});
const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
